import { useSearch } from "@tanstack/react-router";
import { useGetConversionStateQuery } from "api/TenantSlug/ConversionState/hooks.ts";
import { payoutApi } from "api/TenantSlug/Payout/api.ts";
import { createGeneralInfoList } from "components/Drawers/Logs/shared/createGeneralInfoList.tsx";
import { LogCustomParametersSection } from "components/Drawers/Logs/shared/LogCustomParametersSection.tsx";
import { LogTransactionsSection } from "components/Drawers/Logs/shared/LogTransactionsSection.tsx";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList.tsx";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useMemo } from "react";
import { DrawerId } from "types/models.ts";

export const LogClickDrawerDetail: FC = () => {
    const { t } = usePaldockTranslation("drawers.log-click-detail");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tBase } = usePaldockTranslation();
    const id = DrawerId.LogClickDetail;

    const params: { logClickDetail?: string } = useSearch({ strict: false });

    const getConversionQuery = useGetConversionStateQuery(
        params?.logClickDetail || "",
        {
            filter: { include: "country,owner,offer,advertiser" },
        },
        {
            enabled: !!params?.logClickDetail,
        }
    );

    const { conversion } = getConversionQuery.data || {};

    const transactionsPayload = useMemo(() => {
        if (!conversion?.origin_eid) return null;
        return {
            filter: {
                sort: "-created_at",
                filter: { origin_eid: conversion.origin_eid },
                include:
                    "currency,children,children.currency,commission,conversionState,conversionState.owner,conversionState.advertiser,conversionState.offer,conversionState.country,conversionState.cascade",
            },
        };
    }, [conversion?.origin_eid]);

    const getTransactionsQuery = useFetchAllPaginatedData(
        (payout) => payoutApi.index(payout),
        transactionsPayload || {},
        "payouts",
        { enabled: !!(conversion && conversion.origin_eid && transactionsPayload) }
    );

    const generalInfoList = createGeneralInfoList(conversion, tBase);

    const loading = getConversionQuery.isLoading || getTransactionsQuery.isLoading;

    if (!params?.logClickDetail) {
        return null;
    }

    return (
        <Drawer
            loading={loading}
            drawerId={id}
            title={<DrawerTitle mainTitle={t("title")} />}
            searchParamsToClean={["logClickDetail"]}
            showCancelButton={true}
        >
            <DrawerContent>
                <DrawerCollapsible title={tC("general")}>
                    <PropertiesList list={generalInfoList} />
                </DrawerCollapsible>

                <LogCustomParametersSection conversion={conversion} />

                <LogTransactionsSection payouts={getTransactionsQuery.data ?? []} loading={loading} />
            </DrawerContent>
        </Drawer>
    );
};
